import React, { useState, useRef, useEffect } from "react";
import { IoSend } from "react-icons/io5";
import { enviarMensagem, listarMensagens } from "../Services/Chat/enviarService";

export default function AreaConversa({
  usuarioAtivo,
  historico,
  onNovaMensagem,
  usuarioLogado,
  limparHistorico,
}) {
  const [mensagem, setMensagem] = useState("");
  const mensagensRef = useRef(null);
  const [enviando, setEnviando] = useState(false);

  const REMETENTE_ID = usuarioLogado?.id || 1;

  // 🔹 Carregar mensagens reais ao trocar de usuário ativo
  useEffect(() => {
    async function carregarMensagens() {
      if (!usuarioAtivo?.id || !REMETENTE_ID) return;

      try {
        const mensagensBanco = await listarMensagens(
          REMETENTE_ID,
          usuarioAtivo.id
        );

        // ✅ Limpa o histórico antes de atualizar (evita duplicação)
        limparHistorico(usuarioAtivo.id);

        // ✅ Adiciona mensagens do banco (de ambos os lados)
        mensagensBanco.forEach((msg) => {
          onNovaMensagem(usuarioAtivo, {
            texto: msg.mensagem,
            autor:
              msg.remetente_id === REMETENTE_ID
                ? "Você"
                : msg.remetente_nome || usuarioAtivo.nome,
            hora: new Date(msg.data_envio).toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          });
        });
      } catch (error) {
        console.error("Erro ao carregar mensagens:", error);
      }
    }

    carregarMensagens();
  }, [usuarioAtivo?.id, REMETENTE_ID]);

  // 🔹 Enviar mensagem
  const handleSubmit = async (e) => {
    e.preventDefault();
    const msgTexto = String(mensagem || "").trim();
    if (!msgTexto || !usuarioAtivo || enviando) return;

    const novaMensagem = {
      texto: msgTexto,
      autor: "Você",
      hora: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    // Adiciona imediatamente no histórico local
    onNovaMensagem(usuarioAtivo, novaMensagem);
    setMensagem("");

    try {
      setEnviando(true);
      await enviarMensagem({
        remetente_id: REMETENTE_ID,
        destinatario_id: usuarioAtivo.id,
        mensagem: msgTexto,
      });
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      alert("Falha ao enviar mensagem. Verifique sua conexão.");
    } finally {
      setEnviando(false);
    }
  };

  // 🔹 Scroll automático sempre que o histórico mudar
  useEffect(() => {
    if (mensagensRef.current) {
      mensagensRef.current.scrollTop = mensagensRef.current.scrollHeight;
    }
  }, [historico, usuarioAtivo]);

  const mensagens = historico[usuarioAtivo?.id] || [];

  return (
    <div className="flex-1 flex flex-col bg-[#f9f9f9]">
      {/* Cabeçalho */}
      <div className="bg-[#4d4d4d] text-white p-3 flex items-center justify-between shadow-sm">
        <h3
          className="text-lg font-semibold tracking-wide text-center w-full"
          style={{ fontFamily: "'Comfortaa', cursive" }}
        >
          {usuarioAtivo
            ? `Conversa com: ${usuarioAtivo.nome} 💬`
            : "Nenhum usuário selecionado"}
        </h3>
      </div>

      {/* Mensagens */}
      <div
        ref={mensagensRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 bg-white"
        style={{
          backgroundImage:
            "url('https://www.transparenttextures.com/patterns/paper-fibers.png')",
        }}
      >
        {!usuarioAtivo ? (
          <p className="text-gray-500 text-center mt-4">
            Escolha um usuário para iniciar a conversa.
          </p>
        ) : mensagens.length === 0 ? (
          <p className="text-gray-500 text-center mt-4">
            Nenhuma mensagem ainda.
          </p>
        ) : (
          mensagens.map((msg, i) => (
            <div
              key={i}
              className={`flex ${
                msg.autor === "Você" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`px-4 py-2.5 rounded-2xl text-sm shadow-sm max-w-[75%] ${
                  msg.autor === "Você"
                    ? "bg-[#dcf8c6] text-gray-900 rounded-br-none"
                    : "bg-white text-gray-800 rounded-bl-none border border-gray-200"
                }`}
              >
                <p className="whitespace-pre-line break-words mb-1 leading-snug">
                  {msg.texto}
                </p>
                <div className="text-[10px] text-gray-500 text-right mt-1">
                  {msg.hora}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Campo de envio */}
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 border-t border-gray-300 p-3 bg-gray-50"
      >
        <input
          type="text"
          placeholder={
            usuarioAtivo
              ? `Mensagem para ${usuarioAtivo.nome}...`
              : "Selecione um usuário..."
          }
          value={mensagem}
          onChange={(e) => setMensagem(String(e.target.value))}
          disabled={!usuarioAtivo || enviando}
          className="flex-1 px-4 py-2 rounded-full bg-white text-gray-800 border border-gray-300 outline-none disabled:bg-gray-100"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) handleSubmit(e);
          }}
        />

        <button
          type="submit"
          disabled={!usuarioAtivo || enviando}
          className={`p-2.5 rounded-full transition-all duration-150 ${
            usuarioAtivo
              ? "bg-[#4d4d4d] hover:bg-gray-700 text-white"
              : "bg-gray-400 cursor-not-allowed text-gray-200"
          }`}
        >
          <IoSend size={18} />
        </button>
      </form>
    </div>
  );
}
